import React, { memo, useEffect, useReducer, useMemo, useCallback, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { InlineMessage } from 'transfix-ui/components/InlineMessage'
import { useRefresh } from 'utils/hooks/useRefresh'
import FacilityDisplayTile from 'apps/fleet-planner/components/atoms/FacilityDisplayTile'
import SuggestFacility, {
  IFacilityOption,
} from 'apps/fleet-planner/components/form-components/SuggestFacility'
import { FacilityForm } from 'apps/fleet-planner/components/forms/FacilityForm'
import { AddEntityModal } from 'apps/fleet-planner/components/modals/AddEntityModal'
import { FP_SUPPORT_EMAIL } from 'apps/fleet-planner/config'
import { useFMSAnalytics } from 'apps/fleet-planner/hooks'
import { useFacilityApi } from 'apps/fleet-planner/hooks/useFacilityApi'
import AddFacilityAddressForm from 'apps/fleet-planner/pages/loads/AddFacilityAddressForm'
import { getUpdatedStop } from 'apps/fleet-planner/pages/loads/AddNewFacilityForm'
import { createNewAddressRequest } from 'apps/fleet-planner/schema'
import {
  FacilitySchemaType,
  createNewFacilityRequest,
} from 'apps/fleet-planner/schema/FacilitySchema'
import * as Types from 'apps/fleet-planner/types'
import {
  createNewFacilitySuccess,
  getRecentFacilities,
} from 'apps/fleet-planner/utils/actions/facilities'
import { IFacility, IFacilityAddress } from 'apps/fleet-planner/utils/api'
import { CreateFacilityAddressSchema } from 'apps/fleet-planner/utils/createFacilityAddressValidator'
import { CreateFacilitySchema } from 'apps/fleet-planner/utils/createFacilityValidator'
import { getFacilityDefaultValues } from 'apps/fleet-planner/utils/getFacilityDefaultValues'
import {
  facilityByIdSelector,
  recentFacilitiesStateSelector,
} from 'apps/fleet-planner/utils/selectors/facilities'
import { IStore } from 'apps/fleet-planner/utils/types/store'
import { TextInputIconType } from 'components/atoms'
import { AddIcon } from 'transfix-icons'
import { InlineEditContainer } from 'transfix-ui'
import { lookupCountryByStateCode } from 'utils/getLocations'
import { COUNTRIES } from 'constants/countriesStates'

const getMatchingFacility = (
  facilities: IFacility[],
  facilityId?: string
): IFacility | undefined => {
  return facilities.find(f => facilityId && f.id === facilityId)
}

const INPUT_NAMES = {
  facility: 'facility',
  address: 'address',
  none: '',
}

export interface IFacilityProps {
  stop: Partial<Types.IStop>
  isFacility?: boolean
  isAddresss?: boolean
  isInlineEditMode?: boolean
  onBlur?: () => void
  onStopChange: (stop: Partial<Types.IStop>, isNewFacility?: boolean) => void
  errors?: {
    [key: string]: string
  }
  dataTest?: string
  onValidationChange?: (val?: string) => void
  label?: string
  placeholder?: string
  /**
   * @default true
   */
  large?: boolean
  /**
   * @default true
   */
  required?: boolean
  /**
   * @default 'search'
   */
  icon?: keyof typeof TextInputIconType
}

export enum FormModels {
  facility = 'facility',
  address = 'address',
  none = 'none',
}

interface FormReducerState {
  activeModel: FormModels
  isEditing: boolean
  isNew: boolean
}

const defaultReducerState: FormReducerState = {
  activeModel: FormModels.none,
  isEditing: false,
  isNew: false,
}

const getDefaultState = (
  stop: Partial<Types.IStop>,
  isInlineEditMode?: boolean
): FormReducerState => {
  const hasFacility = !!stop.facility?.name
  const hasAddress = !!Object.keys({ ...stop?.address }).length

  let isEditing = false
  let activeModel = FormModels.none // default
  if (hasFacility) {
    activeModel = FormModels.facility
  } else if (hasAddress) {
    activeModel = FormModels.address
    isEditing = !!isInlineEditMode
  }

  return {
    ...defaultReducerState,
    activeModel,
    isEditing,
  }
}

export enum ReducerActions {
  addFacility = 'addFacility',
  editFacility = 'editFacility',
  displayFacility = 'displayFacility',
  editAddress = 'editAddress',
  addAddress = 'addAddress',
  displayAddress = 'displayAddress',
  reset = 'reset',
  setEditingFalse = 'setEditingFalse',
  setEditingTrue = 'setEditingTrue',
}

const facilityInlineReducer = (
  state: FormReducerState = defaultReducerState,
  type: ReducerActions
) => {
  switch (type) {
    case ReducerActions.addFacility:
      return {
        activeModel: FormModels.facility,
        isEditing: true,
        isNew: true,
      }
    case ReducerActions.editFacility:
      return {
        activeModel: FormModels.facility,
        isEditing: true,
        isNew: false,
      }
    case ReducerActions.displayFacility:
      return {
        activeModel: FormModels.facility,
        isEditing: false,
        isNew: false,
      }
    case ReducerActions.editAddress:
      return {
        activeModel: FormModels.address,
        isEditing: true,
        isNew: false,
      }
    case ReducerActions.addAddress:
      return {
        activeModel: FormModels.address,
        isEditing: true,
        isNew: true,
      }
    case ReducerActions.displayAddress:
      return {
        activeModel: FormModels.address,
        isEditing: false,
        isNew: false,
      }
    case ReducerActions.reset:
      return {
        activeModel: FormModels.none,
        isEditing: false,
        isNew: false,
      }
    case ReducerActions.setEditingFalse:
      return {
        ...state,
        isEditing: false,
      }
    case ReducerActions.setEditingTrue:
      return {
        ...state,
        isEditing: true,
      }
    default:
      return state
  }
}

const defaultNewAddressState: CreateFacilityAddressSchema = {
  id: undefined, // when undefined, this value means its a new address
  addressee: '',
  addressLine1: '',
  addressLine2: '',
  locality: '',
  subdivisionCode: '',
  postalCode: '',
}

export const Facility = memo<IFacilityProps>(
  ({
    dataTest,
    errors,
    icon: iconProp,
    isInlineEditMode,
    label: labelProp = 'Facility',
    large: largeProp = true,
    onBlur: onBlurProp,
    onStopChange: onStopChangeProp,
    onValidationChange: onValidationChangeProp,
    placeholder: placeholderProp = 'Choose a facility',
    required: requiredProp = true,
    stop,
  }) => {
    const { trackEvent } = useFMSAnalytics()
    const { address, stopType } = stop || {}
    const {
      createNewFacility,
      error: apiError,
      isLoading: isApiLoading,
      updateFacilityInfo,
    } = useFacilityApi()

    const [defaultFacilityName, setDefaultFacilityName] = useState<string>('')
    // this is the object used and edited in forms
    const [formStop, setFormStop] = useState<Partial<Types.IStop>>(stop)

    // Used to completely re-init component state when
    // cancelling the Add Facility form
    const { key, resetKey } = useRefresh()

    const defaultState = getDefaultState(stop, isInlineEditMode)
    const [{ activeModel, isEditing }, setDisplayState] = useReducer(
      facilityInlineReducer,
      defaultState
    )
    const { facilities } = useSelector(recentFacilitiesStateSelector)
    const dispatch = useDispatch()
    const currentStopFacility = useSelector<IStore, IFacility | undefined>(state =>
      facilityByIdSelector(state, `${stop?.facility?.id}`)
    )

    const facilityContact = useMemo(() => {
      const { facilityContact: defaultContact } = formStop || {}
      const { contacts = [] } = currentStopFacility || {}
      const [contact] = contacts
      return defaultContact || contact
    }, [formStop, currentStopFacility])

    // Fetch recent facilities for use in other components
    useEffect(() => {
      if (!facilities?.length) dispatch(getRecentFacilities({ pageSize: 1500 }))
    }, [dispatch, facilities?.length])

    // Update the form stop if stop prop is changed
    useEffect(() => {
      setFormStop(stop)
    }, [stop])

    /**
     * Clear all `facility` and `address` info from `Stop`
     */
    const resetStopFacility = useCallback(() => {
      onStopChangeProp({
        address: undefined,
        facility: undefined,
        facilityContact: undefined,

        // Legacy facility address model
        stateCode: undefined,
        city: undefined,
      })
    }, [onStopChangeProp])

    /**
     * Clear all `facility` and `address` info from `Stop`
     */
    const resetFormStop = useCallback(() => {
      setFormStop({
        address: undefined,
        facility: undefined,
        facilityContact: undefined,

        // Legacy facility address model
        stateCode: undefined,
        city: undefined,
      })
    }, [setFormStop])

    const addFacilityAddressHandler = () => {
      setDisplayState(ReducerActions.displayAddress)
    }

    const cancelFacilityAddressHandler = () => {
      setDisplayState(ReducerActions.displayAddress)
    }

    const addressFormChangeHandler = ({
      addressee = '',
      isSavingFacility,
      ...change
    }: CreateFacilityAddressSchema) => {
      const update: {
        address: Partial<IFacilityAddress>
        facility?: {
          name?: string
          id?: string
        }
      } = {
        address: {
          // Copy any previous info
          ...stop.address,

          // Include other changes too
          ...change,

          // When `id` is included, it blocks backend from
          // updating Stop address, since it references a
          // specific address entity.
          // Use `undefined` here to ensure you can update the
          // stop address.
          id: undefined,
        },
      }

      // If `facility.name` is included, the Facility will be saved for later too
      if (addressee) {
        update.facility = {
          name: addressee,
        }
        update.address.addressee = addressee
      }

      const updatedStop = getUpdatedStop(update)
      onStopChangeProp(updatedStop, isSavingFacility)
    }

    const facilityFormEditRequestedHandler = () => {
      // If there's an id, it means the facility exists already
      // Otherwise, it's a new Facility
      if (stop.facility?.id) {
        setDisplayState(ReducerActions.editFacility)

        // Copy the data over to our local state
        let update: CreateFacilitySchema = { ...stop.address }
        const { facility } = stop || {}
        const { name: facilityName } = facility || {}
        const { email, name, phone, phoneExt } = facilityContact || {}

        // If there are customer details, add them
        if (facilityContact) {
          update = {
            ...update,
            email,
            phone,
            phoneExt,
            name,
          }
        }

        if (facilityName) {
          update.addressee = facilityName
        }
      } else {
        setDisplayState(ReducerActions.addFacility)
      }
    }

    const addFacilityHandler = (facility: Partial<IFacility>) => {
      const { addresses, contacts, id, name } = facility || {}
      const [contact] = contacts || []
      const [facilityAddress] = addresses || []
      const update = getUpdatedStop({
        address: {
          ...facilityAddress,

          // Use facility name for this form
          addressee: name,
        },
        facilityContact: contact,
        facility: {
          id,
          name,
        },
      })

      onStopChangeProp(update)

      setDisplayState(ReducerActions.displayFacility)
    }

    const changeFacilityHandler = useCallback(
      ({ address: facilityAddress, facility }: IFacilityOption = {}) => {
        let nextAddress: Partial<IFacilityAddress> = facilityAddress || {}
        let nextContact: Types.ICustomerContact | undefined

        const matchingFacility = getMatchingFacility(facilities, facility?.id)

        // If there's a match, use that info
        if (matchingFacility && !!facility) {
          const { addresses = [], contacts = [] } = matchingFacility || {}
          nextAddress = addresses[0]
          nextContact = contacts[0]
        }

        const update = getUpdatedStop({
          address: nextAddress,
          facilityContact: nextContact,
          facility,
        })

        onStopChangeProp(update)

        setDisplayState(ReducerActions.displayFacility)
      },
      [facilities, onStopChangeProp]
    )

    /**
     * Optionally reset with a default `addressee` value from <SuggestFacility />
     */
    const createFacilityHandler = useCallback((inputValue: string = '') => {
      const isAddCustomer = inputValue === ''
      if ((inputValue && inputValue.length) || isAddCustomer) {
        resetFormStop()
        setDefaultFacilityName(inputValue)
      }
      setDisplayState(ReducerActions.addFacility)
    }, [])

    const createAddressHandler = useCallback(
      ({ address: facilityAddress }: IFacilityOption = {}) => {
        resetStopFacility()

        const { locality, postalCode, subdivisionCode } = facilityAddress || {}
        const update = getUpdatedStop({
          address: {
            ...defaultNewAddressState,
            locality,
            postalCode,
            subdivisionCode,
          },
        })

        const countryCode = lookupCountryByStateCode(subdivisionCode)

        onStopChangeProp({
          ...update,
          // Remove any existing data for facility
          facility: undefined,
          countryCode: countryCode ? countryCode.code : COUNTRIES[0].code,
        })

        setDisplayState(ReducerActions.addAddress)
      },
      [onStopChangeProp, resetStopFacility]
    )

    const clearFacilityHandler = useCallback(() => {
      resetStopFacility()
      setDefaultFacilityName('')
      setDisplayState(ReducerActions.reset)
    }, [resetStopFacility])

    const noOpHandler = () => {}

    const addNewFacilityClickHandler = () => createFacilityHandler()

    const getValue = useCallback(() => {
      const { locality: city, postalCode, subdivisionCode: state } = address || {}
      const { facility } = stop || {}
      const { id, name } = facility || {}

      return {
        id,
        city,
        state,
        name,
        postalCode: postalCode ?? '',
      }
    }, [address, stop])

    const value = useMemo(() => getValue(), [getValue])

    const errorMessage = useMemo(
      () =>
        errors?.locality ||
        errors?.subdivisionCode ||
        errors?.city ||
        errors?.stateCode ||
        errors?.facility,
      [errors?.city, errors?.locality, errors?.stateCode, errors?.subdivisionCode, errors?.facility]
    )

    const submitAddFacilityHandler = async (data: FacilitySchemaType) => {
      const req = createNewFacilityRequest(data)
      const res = await createNewFacility(req)

      if (res) {
        dispatch(createNewFacilitySuccess(res))
        addFacilityHandler(res)
      }
    }

    const submitUpdateFacilityHandler = async (facilityId: string, data: FacilitySchemaType) => {
      const newAddress = createNewAddressRequest(data.address, { addressee: data.name })
      const newContact = { ...data.contact }

      /**
       * Triggers Redux update
       */
      await updateFacilityInfo(
        facilities,
        facilityId,
        stop.address,
        stop.facilityContact,
        newAddress,
        newContact,
        (updatedFacility: IFacility) => {
          addFacilityHandler(updatedFacility)
        }
      )
    }

    const submitFacilityForm = async (data: FacilitySchemaType) => {
      if (stop.facility?.id) {
        await submitUpdateFacilityHandler(stop.facility.id, data)
      } else {
        await submitAddFacilityHandler(data)
      }
    }

    const closeFacilityFormHandler = () => {
      resetKey()
      // on cancel, reset the form stop to the original version
      setFormStop(stop)
      setDisplayState(ReducerActions.setEditingFalse)
    }

    const facilityDefaultValues = useMemo(
      () =>
        getFacilityDefaultValues(
          formStop.facility?.name || defaultFacilityName,
          formStop.address,
          formStop.facilityContact
        ),
      [defaultFacilityName, formStop.address, formStop.facility?.name, formStop.facilityContact]
    )

    const facilitiesBulkUploadClickHandler = () =>
      trackEvent('Bulk Upload', 'CLICK_FACILITIES_BULK_UPLOAD_LINK')

    return (
      <>
        <SuggestFacility
          key={key}
          label={labelProp}
          id={`${stopType}-city`}
          placeholder={placeholderProp}
          value={value}
          locationTypes='place,postcode,locality'
          large={largeProp}
          required={requiredProp}
          errorMessage={errorMessage}
          invalid={!!errorMessage}
          countryCodes='us'
          onChangeFacility={changeFacilityHandler}
          onCreateFacility={createFacilityHandler}
          onCreateAddress={createAddressHandler}
          onClearFacility={clearFacilityHandler}
          onBlur={onBlurProp}
          disabled={isEditing}
          dataTest={dataTest}
          onValidationChange={onValidationChangeProp}
          icon={iconProp}
        />

        {!formStop?.facility?.name && (
          <Button
            startIcon={<AddIcon />}
            variant='text'
            data-test='add-new-facility-cta'
            onClick={addNewFacilityClickHandler}>
            Add new facility
          </Button>
        )}

        <div className='mt2'>
          {activeModel === FormModels.address && (
            <InlineEditContainer
              className='mb2 inline-edit-container-suggest-facility'
              name={INPUT_NAMES.address}
              editModeEnabled={isEditing && activeModel === FormModels.address}
              onEditModeRequested={noOpHandler}
              editMode={
                <AddFacilityAddressForm
                  changeHandler={addressFormChangeHandler}
                  errors={errors}
                  facilityAddress={formStop.address || {}}
                  addFacilityAddress={addFacilityAddressHandler}
                  cancelFacilityAddress={cancelFacilityAddressHandler}
                  isEditing={isEditing}
                />
              }>
              <FacilityDisplayTile
                facilityAddress={formStop.address || {}}
                facilityName={formStop.facility?.name || formStop.address?.addressee}
                facilityContact={facilityContact}
                isEditModeEnabled
                isLoading={isApiLoading}
                onEdit={facilityFormEditRequestedHandler}
              />
            </InlineEditContainer>
          )}

          {activeModel === FormModels.facility && (
            <>
              {formStop.facility?.id && (
                <FacilityDisplayTile
                  facilityAddress={formStop.address || {}}
                  facilityName={formStop.facility?.name || formStop.address?.addressee}
                  facilityContact={facilityContact}
                  isEditModeEnabled
                  isLoading={isApiLoading}
                  onEdit={facilityFormEditRequestedHandler}
                />
              )}
              {isEditing && (
                <AddEntityModal
                  open
                  title={`${formStop.facility?.id ? 'Edit' : 'Add New'} Facility`}
                  error={apiError}
                  onClose={closeFacilityFormHandler}>
                  {!formStop.facility?.id && (
                    <InlineMessage
                      className='mt3 mt0-m'
                      message={
                        <span>
                          <b>Lots of facilities to add?</b>{' '}
                          <a
                            href={`mailto:${FP_SUPPORT_EMAIL}`}
                            onClick={facilitiesBulkUploadClickHandler}>
                            Contact us
                          </a>{' '}
                          and we’ll import your facilities in bulk.
                        </span>
                      }
                    />
                  )}
                  {formStop.facility?.id && (
                    <div className='mb4'>Changes to this facility will only affect this load.</div>
                  )}
                  {/* Facility Info */}
                  {formStop.facility?.name && (
                    <div className='mb4'>
                      <div className='body-medium b mb1'>{formStop.facility?.name}</div>
                      <div className='mb4'>
                        <div>{formStop.address?.addressLine1 ?? ''}</div>
                        {!!formStop.address?.addressLine2 && (
                          <div>{formStop.address?.addressLine2 ?? ''}</div>
                        )}
                        <div>
                          {formStop.address?.locality ?? ''}, {formStop.address?.subdivisionCode}{' '}
                          {formStop.address?.postalCode}
                        </div>
                      </div>
                    </div>
                  )}
                  <FacilityForm
                    defaultValues={facilityDefaultValues}
                    onSuccess={submitFacilityForm}
                    onCancel={closeFacilityFormHandler}
                  />
                </AddEntityModal>
              )}
            </>
          )}
        </div>
      </>
    )
  }
)
